import {
  Box,
  Button,
  FormControlLabel,
  Slider,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { palette } from "../../themes/theme";
import { useRef } from "react";
import { CustomPolygonClass } from "./utilities/CustomPolygon";
import PrimaryTextField from "../PrimaryTextField";
import DarkPrimaryButton from "../PrimaryButton/DarkPrimaryButton";
import { CustomDraggable } from "../Draggable";
import { getCenterCoordinates, MaximumLTA } from "../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import { removeTradeArea } from "../../redux/slices/mapSlice";
import { CustomMarkerClass } from "./utilities/CustomMarker";
import ColorPicker from "../ColorPicker";

export const PolygonStyleSetting = ({
  mapRef,
  polygonSelected,
  setPolygonSelected,
  drawRef,
  currentModeRef,
  setMarkers,
  markers,
  resetAllOnceChanged,
  lastClickButtonRef,
  resetActiveId,
  markerRef,
}) => {
  const mapState = useSelector((state) => state.map);

  const dispatch = useDispatch();

  const featureRef = useRef(drawRef.get(polygonSelected.id));
  const polygonStyleRef = useRef({
    polygonColor: polygonSelected.polygonColor,
    polygonBorderColor: polygonSelected.polygonBorderColor,
    polygonOpacity: parseFloat(polygonSelected.polygonOpacity),
    polygonBorderWidth: parseFloat(polygonSelected.polygonBorderWidth),
    polygonColorTransparent: polygonSelected.polygonColorTransparent,
    polygonBorderColorTransparent:
      polygonSelected.polygonBorderColorTransparent,
  });

  const changeStyle = (type, event) => {
    switch (type) {
      case "transparency":
        polygonStyleRef.current.polygonOpacity = event.target.value / 100;
        break;
      case "background-color":
        polygonStyleRef.current.polygonColor = event;
        break;
      case "background-color-transparent":
        polygonStyleRef.current.polygonColorTransparent = event.target.checked;
        break;
      case "border-color-transparent":
        polygonStyleRef.current.polygonBorderColorTransparent =
          event.target.checked;
        break;
      case "border-color":
        polygonStyleRef.current.polygonBorderColor = event;
        break;
      case "border-width":
        polygonStyleRef.current.polygonBorderWidth = Number(event.target.value);
        break;
      default:
        break;
    }
    CustomPolygonClass.onChangePolygonStyle({
      polygonStyle: polygonStyleRef.current,
      mapDraw: drawRef,
      map: mapRef,
      polygonSelected,
      setPolygonSelected,
      setDrawnFeaturesSupport: () => {},
      currentMode: currentModeRef,
    });
  };

  const handleRemove = () => {
    drawRef.delete(featureRef.current.id);
    setPolygonSelected(null);
  };

  const handleOk = () => {
    mapRef.getCanvas().style.cursor = "grab";
    setPolygonSelected(null);
  };

  const addTradeArea = (val) => {
    const id = polygonSelected.id;
    const coordinates = polygonSelected.coordinates;

    setPolygonSelected((prev) => ({
      ...prev,
      tradeArea:
        mapState.tradeAreas.length >= MaximumLTA
          ? !val
            ? val
            : polygonSelected.tradeArea
          : val,
    }));
    if (val) {
      if (mapState.tradeAreas.length < MaximumLTA) {
        const [longitude, latitude] = getCenterCoordinates(coordinates);
        CustomMarkerClass.AddMarkerToPolygon({
          id,
          coordinates: { longitude, latitude },
          type: "polygon-trade-area",
          dispatch,
          mapRef,
          name: "Trade Area",
          radiusSetting: [],
          lastClickButton: lastClickButtonRef,
          resetAllOnceChanged,
          resetActiveId,
          drawRef,
          polygonCoordinates: coordinates,
          markerRef,
        });
        resetAllOnceChanged();
        setMarkers(markerRef.current);
      }
      // const filterHistory = undoHistory.find(data=> data.type === 'polygon' && data.action === 'created' && data.id === id);
      // if(filterHistory) {
      //     filterHistory.currentValue.properties["trade-area"] = true;
      // }
    } else {
      const feature = drawRef.get(id);
      feature.properties["trade-area"] = val;
      drawRef.add(feature);
      const updatedMarkersList = markers.filter((item) => item.id !== id);
      const markerToBeRemoved = markers.filter((item) => item.id === id);
      if (
        markerToBeRemoved &&
        markerToBeRemoved.length &&
        markerToBeRemoved[0].marker
      ) {
        markerToBeRemoved[0].marker.remove();
      }
      markerRef.current = updatedMarkersList;
      setMarkers(updatedMarkersList);
      dispatch(removeTradeArea(id));
      // const filterHistory = undoHistory.find(
      //   (data) =>
      //     data.type === "polygon" && data.action === "created" && data.id === id
      // );
      // if (filterHistory) {
      //   filterHistory.currentValue.properties["trade-area"] = false;
      // }
    }
  };

  return (
    <CustomDraggable
      width={300}
      height={300}
      headerTitle={"Outline Settings"}
      disableOverflow={true}
      customFooter={
        <>
          <Button
            variant="outlined"
            sx={{ fontWeight: "bold" }}
            onClick={handleOk}
          >
            Ok
          </Button>
          <Button
            variant="outlined"
            sx={{ fontWeight: "bold" }}
            onClick={handleRemove}
          >
            Remove
          </Button>
        </>
      }
    >
      <Box sx={{ px: "2px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <ColorPicker
            title={"Fill"}
            checked={polygonStyleRef.current.polygonColorTransparent}
            color={polygonStyleRef.current.polygonColor}
            setColor={changeStyle}
            type={"background-color"}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <ColorPicker
              title={"Border"}
              checked={polygonStyleRef.current.polygonBorderColorTransparent}
              color={polygonStyleRef.current.polygonBorderColor}
              setColor={changeStyle}
              type={"border-color"}
            />
            <PrimaryTextField
              type="number"
              sx={{
                width: "65px",
              }}
              label="Size"
              showSpinBtn={false}
              defaultValue={polygonStyleRef.current.polygonBorderWidth}
              onChange={(e) => changeStyle("border-width", e)}
            />
          </Box>
          <Typography sx={{ color: palette.text.dark, fontSize: 15 }}>
            Transparency:
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <Slider
              min={0}
              step={1}
              max={100}
              defaultValue={polygonSelected.polygonOpacity * 100}
              valueLabelDisplay="auto"
              onChange={(e) => changeStyle("transparency", e)}
              sx={{ width: "85%" }}
            />
          </Box>
          <FormControlLabel
            sx={{ color: palette.text.dark }}
            control={
              <Switch
                defaultChecked={
                  featureRef.current.properties["trade-area"] || false
                }
                onChange={(e) => addTradeArea(e.target.checked)}
              />
            }
            label={<span style={{ fontSize: 13 }}>Add to Trade Areas</span>}
          />
        </Box>
      </Box>
    </CustomDraggable>
  );
};
